import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { EMPTY } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';

import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { GroupsBackend } from '@libs/src/groups/groups.backend';
import { ToastService } from '@libs/src/toast/toast.service';

import { getServerError } from '@libs/src/error-handler/error-handler.helper';
import { UpdateGroupSuccess } from '@libs/src/groups/groups-store/groups.actions';
import { Group, GroupTopicsDescriptions } from '@libs/src/models/group.model';
import { AppState } from '@main-client/src/app/app.interfaces';

import castArray from 'lodash-es/castArray';
import cloneDeep from 'lodash-es/cloneDeep';
import find from 'lodash-es/find';
import findIndex from 'lodash-es/findIndex';
import isEqual from 'lodash-es/isEqual';
import set from 'lodash-es/set';

@Component({
  providers: [DestroySubscriptionsService],
  selector: 'app-hub-config-segmentation-drawer',
  templateUrl: './hub-config-segmentation-drawer.component.html',
})
export class HubConfigSegmentationDrawerComponent implements OnInit {
  @Input() data: {
    group: Group;
    segmentationKey: string;
  };
  readonly maxDescriptionLength = 200;
  readonly defaultDescription = {
    industries:
      'Interests are niche areas that compliment Goals. You will get recommended experiences that enrich your professional expertise, based on selected interests.',
    offerings:
      'Tell us more about what skill areas you want to share insights or work on with your community.',
  };
  categoryDescriptionForm = new UntypedFormControl('');
  isLoading = false;
  isDescriptionUpdated = false;
  description = '';

  constructor(
    private readonly destroy$: DestroySubscriptionsService,
    private readonly drawerRef: NzDrawerRef,
    private readonly groupsBackend: GroupsBackend,
    private readonly store: Store<AppState>,
    private readonly toastService: ToastService,
  ) {}

  ngOnInit() {
    this.initializeCategoryDescription(
      this.data.group,
      this.data.segmentationKey,
    );
    this.categoryDescriptionForm.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        tap((formValue) => {
          this.isDescriptionUpdated = !isEqual(
            formValue.trim(),
            this.description,
          );
        }),
      )
      .subscribe();
  }

  initializeCategoryDescription(group: Group, segmentationKey: string) {
    const description = find(group.field_descriptions, [
      'name',
      segmentationKey,
    ])?.description;
    this.description = description || '';
    this.categoryDescriptionForm.patchValue(
      description || (this.defaultDescription as any)[segmentationKey],
    );
    this.isDescriptionUpdated = !isEqual(
      this.categoryDescriptionForm.value.trim(),
      this.description,
    );
  }

  submitForm(group: Group, segmentationKey: string) {
    this.isLoading = true;
    const description = this.categoryDescriptionForm.value;
    const newFieldDescription: GroupTopicsDescriptions = {
      description,
      name: segmentationKey,
    };
    const updatedFieldDescriptions = group.field_descriptions
      ? this.updateExistingGroupFieldDescriptions(
          cloneDeep(group.field_descriptions),
          newFieldDescription,
          segmentationKey,
        )
      : castArray(newFieldDescription);
    return this.groupsBackend
      .patchGroup(group, {
        field_descriptions: updatedFieldDescriptions,
      })
      .pipe(
        tap((response: Group) => {
          this.store.dispatch(UpdateGroupSuccess({ group: response }));
          this.toastService.showSuccess('Update successful!');
          this.isLoading = false;
          this.closeDrawer();
        }),
        catchError((response: HttpErrorResponse) => {
          this.toastService.showError(getServerError(response));
          this.isLoading = false;
          return EMPTY;
        }),
      )
      .subscribe();
  }

  updateExistingGroupFieldDescriptions(
    fieldDescriptions: GroupTopicsDescriptions[],
    newFieldDescription: GroupTopicsDescriptions,
    segmentationKey: string,
  ) {
    const existingFieldDescriptionIndex = findIndex(
      fieldDescriptions,
      (item) => segmentationKey === item.name,
    );
    return -1 < existingFieldDescriptionIndex
      ? set(fieldDescriptions, existingFieldDescriptionIndex, {
          ...fieldDescriptions[existingFieldDescriptionIndex],
          description: newFieldDescription.description,
        })
      : [...fieldDescriptions, newFieldDescription];
  }

  closeDrawer(): void {
    this.drawerRef.close();
  }
}
